// evaluation-data.js - the evaluation rubric. Field-format rubric in
// EVAL_DATA, goalkeeper rubric in EVAL_DATA_GK. Shape:
//   EVAL_DATA[domain][skill][ageGroup] = [lvl1, lvl2, lvl3, lvl4, lvl5]
// Level names: Developing / Emerging / Secure / Advanced / Impactful. The
// GK rubric has no age banding (one descriptor set across ages).
//
// The English text here is the reference. Dutch descriptors + names live in
// src/i18n/eval-descriptors-nl.json, injected at boot via loadNlDescriptors()
// (kept out of this module so the translation tooling can import it in plain
// Node). getDescriptors/localize* return Dutch when the active language is 'nl'
// and fall back to English per line. The round-trip tool is tools/i18n/eval-matrix.mjs.

import { CONFIG } from './config.js';

// Injected NL overlay: { key: { nl, en } }. Empty until the app loads the JSON.
let _NL = {};
export function loadNlDescriptors(obj) { _NL = obj || {}; }
const _nl = (key) => { const e = _NL[key]; return (e && e.nl) ? e.nl : null; };

// ── CLUB OVERRIDES ───────────────────────────────────────────────────────────
//
// The wording below is the STANDARD rubric and stays the default for every
// club. A club that wants different words for a level gets a row in
// rubric_descriptors, and only that row changes, for that club only.
//
// Deliberately sparse rather than a full copy in the database. Two sources of
// truth for the same 480 sentences drift apart the first time one is edited
// and the other is not, and a full copy would put a network request in front
// of the first render for every player and parent who will never customise
// anything. A club with no overrides loads nothing.
//
// KEYED ON criterion_id, which is what the stable ids were for: an override
// survives the criterion being renamed, because it points at the criterion
// rather than at its label.
// 'criterionId|ageBand|level|lang' -> [{ from, until, text }], newest last.
// A list rather than a single string because a wording is only in force for a
// stretch of time: see loadRubricOverrides.
let _OVERRIDE = new Map();

const _ovKey = (criterionId, ageBand, level, lang) =>
  `${criterionId}|${ageBand || ''}|${level}|${lang}`;

/**
 * Install a club's descriptor overrides.
 *
 * `rows` is whatever DB.rubric.overridesFor() returned:
 * [{ criterion_id, age_band, level, lang, descriptor }]. Called with nothing
 * to clear, which matters on sign-out and when switching club: one club's
 * wording must never survive into another's screens.
 */
export function loadRubricOverrides(rows) {
  _OVERRIDE = new Map();
  (rows || []).forEach(r => {
    if (!r?.criterion_id || !r?.descriptor) return;
    const lvl = Number(r.level);
    if (!(lvl >= 1 && lvl <= 5)) return;
    const key = _ovKey(r.criterion_id, r.age_band, lvl, r.lang || 'en');
    if (!_OVERRIDE.has(key)) _OVERRIDE.set(key, []);
    _OVERRIDE.get(key).push({
      // A row with no window is simply always in force, which is what a
      // pre-versioning row means and what a test usually wants.
      from:  r.valid_from    ? Date.parse(r.valid_from)    : -Infinity,
      until: r.superseded_at ? Date.parse(r.superseded_at) :  Infinity,
      text:  r.descriptor,
    });
  });
  _OVERRIDE.forEach(list => list.sort((a, b) => a.from - b.from));
  return _OVERRIDE.size;
}

/** How many level wordings this club has changed. Counts levels, not versions. */
export function rubricOverrideCount() { return _OVERRIDE.size; }

/**
 * A club's own wording for one level, or null to fall through to the standard.
 *
 * Falls back from the active language to English, because a club that has
 * rewritten a descriptor in English has changed what the level MEANS, and
 * showing the standard Dutch beside a changed English line would describe two
 * different things to two coaches.
 */
function _override(domain, skill, ageBand, level, lang, format, asOf) {
  if (!_OVERRIDE.size) return null;
  const id = criterionIdFor(domain, skill, format);
  if (!id) return null;
  const at = asOf == null ? Date.now()
           : (typeof asOf === 'number' ? asOf : Date.parse(asOf));
  const when = Number.isFinite(at) ? at : Date.now();

  const inForce = (lg) => {
    const list = _OVERRIDE.get(_ovKey(id, ageBand, level, lg));
    if (!list) return null;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].from <= when && when < list[i].until) return list[i].text;
    }
    return null;
  };
  return inForce(lang) ?? inForce('en') ?? null;
}

// Stable translation keys - MUST match tools/i18n/eval-matrix.mjs (which imports
// buildTranslatableEntries from here, so they can never drift).
const _fieldKey  = (domain, skill, ageGroup, i) => `desc|field|${domain}|${skill}|${ageGroup}|${i}`;
const _gkKey     = (domain, skill, i) => `desc|gk|${domain}|${skill}|${i}`;
const _domNameKey  = (domain) => `name|domain|${domain}`;
const _skillNameKey = (skill) => `name|skill|${skill}`;

export const EVAL_DATA = {

  Technical: {
    'Ball carrying': {
      'U11–U12': [
        'Controls ball in space; head mostly down',
        'Controls at tempo sometimes; pressure causes losses',
        'Carries at tempo; protects ball; occasional scan',
        'Carries to draw defender; connects quickly after',
        'Breaks pressure repeatedly; carry creates team advantage',
      ],
      'U14': [
        'Loses control at match speed; head down',
        'Solid in drills; shaky in real pressure',
        'Carries under pressure; protects ball reliably',
        'Carries to create advantage; connects quickly',
        'Consistent line-breaker; carry decides moments',
      ],
      'U16–U19': [
        'Unreliable at pace; turnovers under press',
        'Good until fatigue/press; consistency not there',
        'Consistent match control; protects ball under pressure',
        'Creates advantage under fatigue; connects at speed',
        'Decides games with carry; breaks pressure repeatedly',
      ],
    },

    'Receiving': {
      'U11–U12': [
        'Stops ball; first touch often kills next action',
        'First touch ok in space; rushed when pressed',
        'First touch sets next action; body opens more',
        'Receives open under pressure; plays earlier',
        'Receives at speed under press; keeps tempo high',
      ],
      'U14': [
        'First touch breaks under press; slows play',
        'Good when time; rushed when pressured',
        'Receives clean in matches; sets next action',
        'Receives open + plays early under pressure',
        'Controls tempo from receive; wins time for team',
      ],
      'U16–U19': [
        'First touch breaks under pressure; slows tempo',
        'Ok when time; rushed under heavy press',
        'Clean receive at match speed; sets next action',
        'Receives open + plays early under pressure',
        'Elite under heavy press; receive controls tempo',
      ],
    },

    'Passing': {
      'U11–U12': [
        'Pass weight/aim inconsistent; limits team flow',
        'Good simple passes; struggles when rushed',
        'Reliable push-pass + timing; links play well',
        'Plays fast into advantage; finds better options',
        'Connects team consistently; switches/feeds key moments',
      ],
      'U14': [
        'Rushed passes; weight/aim inconsistent in matches',
        'Solid basics; inconsistent when pressed',
        'Reliable match passing; links play with purpose',
        'Breaks lines; plays faster; switches effectively',
        'Runs game through distribution; key passes repeat',
      ],
      'U16–U19': [
        'Rushed choices/weight; errors under pressure',
        'Good basics; inconsistent under stress',
        'Reliable match distribution; links play with purpose',
        'Breaks lines; changes tempo; finds best option fast',
        'Controls match with passing; key actions repeat weekly',
      ],
    },

    'Elimination (1v1)': {
      'U11–U12': [
        'Forces dribble; loses ball more than wins',
        'Beats defender sometimes; slow to connect after',
        'Eliminates with purpose; connects within 1-2 actions',
        'Fixes defender to create pass/shot advantage',
        'Creates overloads often; elimination leads to chances',
      ],
      'U14': [
        'Forces dribbles; turnovers hurt the team',
        'Beats sometimes; connection after is slow',
        'Eliminates then connects; creates advantage often',
        'Fixes defenders consistently; creates clear overloads',
        'Creates constant overloads; elimination leads to goals',
      ],
      'U16–U19': [
        'Forces plays; turnovers hurt structure',
        'Beats sometimes; connection after inconsistent',
        'Eliminates and connects reliably; creates advantage',
        'Fixes defenders consistently; creates high-value chances',
        'Constant threat; elimination leads to goals and assists',
      ],
    },

    'Defensive skills': {
      'U11–U12': [
        'Dives/lunges; gets beaten; late recoveries',
        'Can delay; tackling technique inconsistent',
        'Angles + channels; wins or delays reliably',
        'Wins clean; protects middle; strong recovery habit',
        'Stops attacks repeatedly; stabilises teammates',
      ],
      'U14': [
        'Gets beaten too easily; tackling is reactive',
        'Delays ok; wins are inconsistent',
        'Channels + wins some clean; centre protected more',
        'Wins duels; protects middle; strong recoveries',
        'Anchors defence; stops attacks and organises others',
      ],
      'U16–U19': [
        'Gets beaten too easily; late decisions',
        'Delays ok; wins inconsistent under pressure',
        'Wins/delays reliably; centre protected; good recovery',
        'Wins clean; reads attackers; controls duels',
        'Shuts down top threats; anchors defensive unit',
      ],
    },

    'Finishing': {
      'U11–U12': [
        'Needs time to shoot; low circle threat',
        'Shoots when free; execution inconsistent',
        'Gets shot off quickly; follows for rebounds',
        'Finishes under pressure; picks better options',
        'Consistent end product; goals/assists from circle work',
      ],
      'U14': [
        'Low threat; slow to shoot under pressure',
        'Threat when time; output inconsistent',
        'Gets shots off in pressure; follows rebounds',
        'Good decisions + rebounds; creates repeat chances',
        'Reliable scorer/creator; circle output decides results',
      ],
      'U16–U19': [
        'Low output; misses chances under pressure',
        'Output comes/goes; needs clearer decisions',
        'Reliable chance creation/finish; good rebounds',
        'Finishes under fatigue/pressure; picks smart options',
        'High-impact end product weekly; decides matches',
      ],
    },
  },

  Tactical: {
    'Positioning': {
      'U11–U12': [
        'Often out of role; passing lines disappear',
        'Basic shape appears; loses it under speed',
        'Holds role; creates lines; supports team structure',
        'Adjusts early; helps others stay organised',
        'Organises team shape; role discipline lifts group',
      ],
      'U14': [
        'Loses structure; role discipline breaks under stress',
        'Understands plan; forgets at match speed',
        'Holds role + team shape; supports structure',
        'Adjusts early; leads structure in units',
        'Makes unit function; organisation improves performance',
      ],
      'U16–U19': [
        'Role discipline inconsistent; structure breaks',
        'Understands plan; execution swings with pressure',
        'Role clarity + structure in matches; dependable',
        'Adjusts match-to-match; leads shape and spacing',
        'Makes team look organised; tactical leader on field',
      ],
    },

    'Off-ball movement': {
      'U11–U12': [
        'Ball-watches; limited support angles',
        'Moves sometimes; timing often late/early',
        'Moves after pass; offers clear angles regularly',
        'Creates space for others; strong timing at tempo',
        'Always provides options; movement drives team attack',
      ],
      'U14': [
        'Static support; options disappear',
        'Moves, timing off; runs don\'t help enough',
        'Good angles + timing; consistent support',
        'Creates space for others; makes overloads',
        'Unlocks team attacks; movement drives advantage',
      ],
      'U16–U19': [
        'Slow support; options arrive late',
        'Good runs; timing inconsistent under stress',
        'Consistent angles + timing; supports possession well',
        'Creates space + overloads; strong timing at pace',
        'Unlocks defence repeatedly; movement drives advantage',
      ],
    },

    'Transition play': {
      'U11–U12': [
        'Slow on turnover; chases emotionally or stops',
        'Reacts sometimes; needs reminders for 3-seconds',
        '3-second reaction often; otherwise recovers shape',
        'Wins balls or recovers smartly; clear roles',
        'Controls transition moments; creates momentum swings',
      ],
      'U14': [
        'Emotional chase/slow recover; chaos after turnover',
        'Reacts but inconsistent; roles unclear in transition',
        'Clear 3-sec or recover; team response more stable',
        'Wins or recovers smartly; clear nearest roles',
        'Drives momentum swings; transition identity is strong',
      ],
      'U16–U19': [
        'Slow recovery/counterpress; gives away easy goals',
        'Reacts, inconsistent in tough phases',
        'Reliable 3-sec or recover decision; roles clear',
        'Wins balls / creates fast attacks; controls moments',
        'Controls momentum and tempo; transition identity leads',
      ],
    },

    'Decision-making': {
      'U11–U12': [
        'Panics/late; chooses risky options often',
        'Sometimes picks right option; rushed under pressure',
        'Usually chooses right risk/tempo for situation',
        'Fast, correct choices under pressure; improves tempo',
        'Decision-making lifts others; creates consistent advantage',
      ],
      'U14': [
        'Risky central losses; tempo unmanaged',
        'Better choices; still rushed under press',
        'Manages risk/tempo well; resets when needed',
        'Fast correct choices; reduces unforced errors',
        'Controls match moments; decisions create stability',
      ],
      'U16–U19': [
        'Cheap central losses; poor match-state choices',
        'Better risk; still rushed late-game',
        'Smart tempo/risk choices; resets when needed',
        'Excellent under pressure + fatigue; low unforced errors',
        'Match-winning decision-maker; controls game states',
      ],
    },

    'Pressing': {
      'U11–U12': [
        'Presses alone; opens centre; no cover',
        'Understands idea; execution messy/inconsistent',
        'Presses with help sometimes; centre-first appears',
        'Presses with cover often; forces predictable outcomes',
        'Leads regains; pressing behaviour sets team tone',
      ],
      'U14': [
        'Presses alone; gaps appear; centre opens',
        'Understands triggers; execution inconsistent',
        'Presses with help + centre first; distances better',
        'Trigger press + unit distances; forces turnovers',
        'Leads regains as a unit; press defines team',
      ],
      'U16–U19': [
        'Presses without cover; gaps and fouls',
        'Knows triggers; inconsistent execution and distances',
        'Presses with cover, centre first; team stable',
        'Trigger press + unit distances consistent; forces regains',
        'Leads press identity + regains; sets defensive tone',
      ],
    },

    'Game awareness': {
      'U11–U12': [
        'Little scanning; reacts after ball arrives',
        'Scans sometimes; sees options late',
        'Scans before receive more often; spots space',
        'Reads space early; anticipates next action',
        'Sees play ahead; dictates tempo and choices',
      ],
      'U14': [
        'Reacts late; scans too late',
        'Reads sometimes; sees space late',
        'Scans + recognises space often; better timing',
        'Anticipates patterns; adjusts before problems',
        'Sees game ahead; makes team calmer and smarter',
      ],
      'U16–U19': [
        'Sees late; reacts to problems after they happen',
        'Reads sometimes; scans not consistent enough',
        'Scans + anticipates often; spots space early',
        'Reads patterns, controls space; adjusts before issues',
        'Dictates play; sees game ahead and controls tempo',
      ],
    },
  },

  Physical: {
    'Speed & acceleration': {
      'U11–U12': [
        'Slow first steps; struggles to win races',
        'Quick bursts; repeat efforts drop quickly',
        'Good first steps; can repeat high efforts',
        'Fast consistently; repeat sprints stay strong',
        'Speed changes games; wins key moments often',
      ],
      'U14': [
        'Struggles with pace; loses key races',
        'Quick bursts; fades across the match',
        'Match-speed repeat efforts; pace holds up',
        'Fast + repeat sprint strong; speed late game too',
        'Speed is decisive; wins key moments regularly',
      ],
      'U16–U19': [
        'Pace not reliable; loses key races late',
        'Quick bursts; fades under fatigue',
        'Match-speed repeat sprint OK; holds pace',
        'Strong repeat sprint; fast late-game too',
        'Speed wins key moments; decisive in transitions',
      ],
    },

    'Coordination & agility': {
      'U11–U12': [
        'Turns/closes body late; loses balance often',
        'Better movement; control drops at high speed',
        'Sharp changes; balanced; skills hold while moving',
        'Agile at speed; stable under contact/pressure',
        'Movement quality stands out; enables high-speed hockey',
      ],
      'U14': [
        'Loses control in turns; unstable under speed',
        'Decent; unstable at high speed or fatigue',
        'Agile and balanced in matches; skills hold',
        'Sharp at high speed; stable in transitions',
        'Movement dominates transitions; agility is a weapon',
      ],
      'U16–U19': [
        'Movement limits skill execution at pace',
        'Decent; unstable under fatigue/pressure',
        'Agile and controlled at pace; skills hold',
        'Sharp, stable in transitions; strong COD',
        'Movement quality is elite; enables high-speed control',
      ],
    },

    'Work rate': {
      'U11–U12': [
        'Effort up/down; recovery runs often missing',
        'Works hard in phases; fades with fatigue',
        'Reliable repeat efforts; consistent defensive work',
        'High intensity often; leads by repeat work',
        'Sets work standard every week; drives team engine',
      ],
      'U14': [
        'Inconsistent intensity; recovery runs missing',
        'Good phases; drops late or after errors',
        'Reliable two-way work; repeats efforts well',
        'High intensity full match; leads by work-rate',
        'Drives team physically; engine shows every week',
      ],
      'U16–U19': [
        'Intensity swings; recovery work inconsistent',
        'Works, drops late; effort not repeatable',
        'Reliable repeat efforts; two-way running consistent',
        'High intensity across match; leads by work',
        'Drives team physically weekly; sets intensity standard',
      ],
    },

    'Stability & strength': {
      'U11–U12': [
        'Gets bumped off ball; loses duels often',
        'Improving duels; still unstable under contact',
        'Holds ground often; stable in tackles/receives',
        'Strong in contact; protects ball; wins duels',
        'Dominates duels; physical presence creates advantage',
      ],
      'U14': [
        'Loses duels; gets moved off line easily',
        'Holds sometimes; inconsistent robustness',
        'Stable in contact; holds ground consistently',
        'Strong duels + ball protection; wins battles',
        'Wins key battles; physical presence changes outcomes',
      ],
      'U16–U19': [
        'Loses duels too often; contact affects play',
        'Holds sometimes; strength not consistent',
        'Strong enough for level; stable in contact',
        'Wins duels consistently; protects ball well',
        'Dominant in key battles; physical presence decides moments',
      ],
    },
  },

  Mental: {
    'Commitment': {
      'U11–U12': [
        'Needs pushing; intensity and routines inconsistent',
        'Tries hard; consistency still forming',
        'Reliable effort; training habits support improvement',
        'Self-driven; high standards; pushes own growth',
        'Sets standard daily; lifts group commitment',
      ],
      'U14': [
        'Effort swings; preparation habits inconsistent',
        'Tries hard; standards not constant yet',
        'Dependable worker; good training habits',
        'High standards daily; pushes own improvement',
        'Sets training culture; commitment lifts group',
      ],
      'U16–U19': [
        'Preparation/effort inconsistent; standards slip',
        'Good intentions; habits inconsistent',
        'Dependable training + match habits; professional baseline',
        'High standards, self-driven; improves daily',
        'Sets performance culture; standards lift the group',
      ],
    },

    'Resilience': {
      'U11–U12': [
        'Mistakes affect next actions; confidence dips',
        'Resets sometimes; emotions still show',
        'Quick reset; stays useful after errors',
        'Strong under pressure; responds well to setbacks',
        'Stabilises others; performs best in tough moments',
      ],
      'U14': [
        'Frustration affects play; errors lead to chaos',
        'Resets with help; still emotional in moments',
        'Resets fast, stays useful; composure improving',
        'Strong in tough moments; stays calm under pressure',
        'Calm leader under pressure; lifts team stability',
      ],
      'U16–U19': [
        'Pressure affects output; mistakes linger',
        'Recovers with time; still dips in big moments',
        'Resets quickly, stays useful; stable mindset',
        'Thrives in tough moments; calm under stress',
        'Big-game mentality; lifts others and delivers',
      ],
    },

    'Focus': {
      'U11–U12': [
        'Switches off; avoidable errors from lapses',
        'Focus comes/goes; pressure reduces attention',
        'Focused most of time; good attention to detail',
        'Focused under fatigue; stays locked in late',
        'Elite consistency; very few concentration errors',
      ],
      'U14': [
        'Switches off; game moments missed',
        'Focus drops under stress; avoidable errors',
        'Stable attention; consistent concentration',
        'Focused under fatigue; stays locked in late',
        'Very few mental errors; reliable every match',
      ],
      'U16–U19': [
        'Lapses cause errors; late-game focus drops',
        'Focus drops under fatigue; avoidable mistakes',
        'Stable concentration; reliable decision focus',
        'Focused late-game; stays locked in pressure phases',
        'Almost no mental mistakes; elite consistency',
      ],
    },

    'Coachability': {
      'U11–U12': [
        'Slow to apply feedback; repeats same issues',
        'Tries changes; not yet consistent',
        'Applies feedback quickly; adjusts within sessions',
        'Seeks feedback; improves fast; adapts at tempo',
        'Learns very fast and helps others apply feedback',
      ],
      'U14': [
        'Repeats same mistakes; slow to adapt',
        'Applies feedback sometimes; inconsistent transfer',
        'Adjusts within sessions; learns through reps',
        'Learns quickly; seeks detail; adapts at speed',
        'Improves rapidly + helps others apply feedback',
      ],
      'U16–U19': [
        'Slow to change; repeats habits',
        'Applies feedback sometimes; not consistent',
        'Adapts quickly; applies feedback in matches',
        'Seeks detail + improves fast; learning is obvious',
        'Accelerates development + supports others\' learning',
      ],
    },

    'Leadership': {
      'U11–U12': [
        'Passive; avoids taking responsibility in moments',
        'Small leadership moments; inconsistent communication',
        'Communicates/supports; takes responsibility when needed',
        'Leads standards; lifts teammates under pressure',
        'Drives team culture; leadership changes outcomes',
      ],
      'U14': [
        'Avoids responsibility; quiet when it\'s hard',
        'Leadership moments; not consistent yet',
        'Communicates + supports; owns mistakes and standards',
        'Leads standards in games; drives calm and effort',
        'Drives group standards; leadership changes the environment',
      ],
      'U16–U19': [
        'Avoids leading; standards not owned',
        'Leads sometimes; inconsistent when it\'s hard',
        'Takes responsibility + communicates; dependable leader',
        'Leads standards under stress; drives calm and effort',
        'Leader who changes outcomes; culture and performance driver',
      ],
    },
  },

  Social: {
    'Attendance': {
      'U11–U12': [
        'Irregular or late; routines need support',
        'Mostly reliable; occasional punctuality gaps',
        'Dependable + prepared; communicates well',
        'Sets routine standard; always ready to train',
        'Model habits; reliability lifts group standards',
      ],
      'U14': [
        'Unreliable routines; availability hurts development',
        'Mostly reliable; occasional gaps in routines',
        'Dependable athlete habits; prepared and punctual',
        'High reliability standard; always ready to train',
        'Professional example; reliability lifts team standards',
      ],
      'U16–U19': [
        'Availability not reliable; impacts team planning',
        'Mostly reliable; occasional gaps in routines',
        'Dependable senior habits; punctual and prepared',
        'Very reliable + prepared; sets routine standard',
        'Model athlete professionalism; reliability lifts the group',
      ],
    },

    'Communication': {
      'U11–U12': [
        'Silent/negative; limited listening and talk',
        'Talks sometimes; inconsistent clarity',
        'Clear + positive; helps teammates with simple cues',
        'Organises teammates; communicates early and calmly',
        'Communication lifts team; creates calm under pressure',
      ],
      'U14': [
        'Quiet/negative under stress; unclear messages',
        'Communicates sometimes; not early enough',
        'Clear, helpful communication; supports unit play',
        'Directs unit play; communicates early and calmly',
        'Communication controls chaos; improves performance',
      ],
      'U16–U19': [
        'Unclear or silent; team organisation suffers',
        'Communicates in moments; not consistent early',
        'Clear, helpful, calm; supports unit decisions',
        'Directs unit play; communicates early under pressure',
        'Controls team under pressure; communication drives performance',
      ],
    },

    'Team spirit': {
      'U11–U12': [
        'Self-focused; limited support for teammates',
        'Supportive sometimes; mood swings affect group',
        'Positive teammate; consistent support and effort',
        'Lifts group; helps others improve and belong',
        'Culture driver; team is better because of her',
      ],
      'U14': [
        'Inconsistent support; group energy affected',
        'Supports sometimes; mood swings still show',
        'Positive teammate; consistent support and respect',
        'Lifts others consistently; builds team confidence',
        'Culture leader; creates strong environment',
      ],
      'U16–U19': [
        'Inconsistent contribution; group energy affected',
        'Supportive sometimes; mood affects standards',
        'Positive, consistent teammate; supports others',
        'Lifts standards and people; builds confidence',
        'Culture leader; creates the environment we want',
      ],
    },

    'Respect & sportsmanship': {
      'U11–U12': [
        'Emotional outbursts; needs reminders on respect',
        'Mostly respectful; occasional frustration lapses',
        'Controlled + respectful; represents club values',
        'Calm under stress; sets example in conflict',
        'Role model always; protects team reputation',
      ],
      'U14': [
        'Emotional reactions; needs reminders',
        'Mostly respectful; occasional frustration lapses',
        'Controlled, good values; respectful in competition',
        'Calm in conflict; sets example for group',
        'Protects team reputation; role model always',
      ],
      'U16–U19': [
        'Emotional reactions; standards slip under stress',
        'Mostly respectful; occasional frustration lapses',
        'Controlled, values-driven; represents club well',
        'Calm in conflict; sets example consistently',
        'Role model always; protects team and club reputation',
      ],
    },
  },

};

// -----------------------------------------------------------------------------
// U10 band (Learn-to-Train, early). U10 teams share the age_group text
// "U11-U12" but get their OWN, simpler descriptors: pitched a notch below
// U11-U12 (mostly unpressured, first game application). Kept as a separate
// block and merged into EVAL_DATA below so the main rubric stays readable.
// Resolved for U10 teams via pillarBandForTeam() in young-age-data.js.
// -----------------------------------------------------------------------------
const U10_DESCRIPTORS = {
  Technical: {
    'Ball carrying': [
      'Ball runs loose; stops to look down at it',
      'Keeps it close when walking; loses it at pace',
      'Carries close at a jog; glances up',
      'Changes direction under control; head comes up',
      'Carries into space and past a player in a game',
    ],
    'Receiving': [
      'Ball bounces off; first touch runs away',
      'Stops a slow ball with the stick down',
      'Controls it and looks up before playing',
      'First touch into space most times',
      'Receives on the move and plays on quickly',
    ],
    'Passing': [
      'Push has no weight or aim',
      'Reaches a nearby teammate when there is time',
      'Weights a push-pass to a free teammate',
      'Passes ahead of a moving teammate',
      'Picks the free teammate and passes on time',
    ],
    'Elimination (1v1)': [
      'Runs straight at the defender and loses it',
      'Beats a still defender now and then',
      'Uses a change of pace or direction to get past',
      'Beats a defender and keeps the ball after',
      'Beats a player in a game and creates a chance',
    ],
    'Defensive skills': [
      'Chases the ball and is easily beaten',
      'Gets goal-side but dives in',
      'Stays low and jockeys instead of lunging',
      'Times the tackle and wins the ball cleanly',
      'Slows or wins the ball for the team in games',
    ],
    'Finishing': [
      'Rushes the shot; misses the target',
      'Shoots when there is space and time',
      'Keeps the shot low and on target more often',
      'Shoots quickly and follows the rebound',
      'Scores from open play in games',
    ],
  },
  Tactical: {
    'Positioning': [
      'Follows the ball in a swarm; loses their spot',
      'Holds a position when reminded',
      'Keeps a simple position and offers a pass line',
      'Finds space to help the ball-carrier',
      'Holds shape and helps the team keep it',
    ],
    'Off-ball movement': [
      'Stands and watches the ball',
      'Moves only after the ball has gone',
      'Shows for a pass after passing',
      'Finds space away from a defender',
      'Times a run to give a clear option',
    ],
    'Transition play': [
      'Stops when the ball is won or lost',
      'Reacts slowly to the turnover',
      'Chases back or forward when it turns over',
      'Reacts quickly to most turnovers',
      'Sprints to win it back or into space at once',
    ],
    'Decision-making': [
      'Freezes or rushes with the ball',
      'Makes a simple choice when there is time',
      'Usually picks pass or dribble sensibly',
      'Chooses well and quickly in simple play',
      'Makes choices that help the team in games',
    ],
    'Pressing': [
      'Chases the ball alone with no plan',
      'Runs at the ball when told',
      'Pressures the ball and stays goal-side',
      'Presses with a teammate to close space',
      'Times the press and helps win it back',
    ],
    'Game awareness': [
      'Sees only the ball',
      'Glances up now and then',
      'Looks up before receiving',
      'Spots a free teammate or space',
      'Reads simple situations and reacts early',
    ],
  },
  Physical: {
    'Speed & acceleration': [
      'Runs at one steady pace',
      'Quicker over short distances when needed',
      'Accelerates onto a loose ball',
      'Quick over the first few steps',
      'Fast and repeats sprints in a game',
    ],
    'Coordination & agility': [
      'Stiff; struggles to turn and stop',
      'Turns and stops with some balance',
      'Changes direction under control',
      'Moves smoothly and balanced with the ball',
      'Agile and coordinated through a game',
    ],
    'Work rate': [
      'Tires quickly and stops',
      'Works in bursts, then rests',
      'Keeps going for most of the session',
      'Works hard up and back',
      'High energy from start to finish',
    ],
    'Stability & strength': [
      'Knocked off balance easily',
      'Stays up in light contact',
      'Holds a steady body position at times',
      'Shields the ball with the body',
      'Balanced and steady in duels for their age',
    ],
  },
  Mental: {
    'Commitment': [
      'Gives up when it gets hard',
      'Tries when things go well',
      'Keeps trying through the session',
      'Full effort in training and games',
      'Always gives their best and lifts others',
    ],
    'Resilience': [
      'Head drops after a mistake',
      'Recovers with encouragement',
      'Shakes off a mistake and carries on',
      'Bounces back quickly and stays positive',
      'Stays confident through ups and downs',
    ],
    'Focus': [
      'Easily distracted',
      'Focuses in short bursts',
      'Concentrates for most of the session',
      'Stays on the task',
      'Concentrates fully, even when tired',
    ],
    'Coachability': [
      'Slow to try what the coach shows',
      'Tries a tip after a reminder',
      "Listens and tries the coach's advice",
      'Applies feedback quickly',
      'Eager to learn and helps others try it',
    ],
    'Leadership': [
      'Waits and follows others',
      'Joins in when encouraged',
      'Encourages a teammate now and then',
      'Sets a good example in effort',
      'Lifts the team by words and actions',
    ],
  },
  Social: {
    'Attendance': [
      'Often misses training',
      'Comes when it suits',
      'Attends most sessions',
      'Rarely misses; arrives on time',
      'Always there, early and ready',
    ],
    'Communication': [
      'Quiet; does not call',
      'Calls a name now and then',
      'Asks for the ball and talks a little',
      'Talks to teammates during play',
      'A clear voice who guides teammates',
    ],
    'Team spirit': [
      'Plays for themselves',
      'Shares the ball sometimes',
      'Plays for the team and shares',
      'Supports and encourages teammates',
      'Brings the team together',
    ],
    'Respect & sportsmanship': [
      'Struggles to accept decisions',
      'Plays fair after reminders',
      'Respects teammates, opponents and coach',
      'Fair and gracious, win or lose',
      'A role model for fair play',
    ],
  },
};

// Merge the U10 band into EVAL_DATA so every skill carries a 'U10' rubric
// alongside 'U11-U12' / 'U14' / 'U16-U19'.
for (const [domain, skills] of Object.entries(U10_DESCRIPTORS)) {
  for (const [skill, rungs] of Object.entries(skills)) {
    if (EVAL_DATA[domain]?.[skill]) EVAL_DATA[domain][skill].U10 = rungs;
  }
}

// -----------------------------------------------------------------------------
// GK rubric (gk_v1). A fully separate skill set for goalkeepers.
//
// Same 5 pillar names as the field rubric (Technical / Tactical / Physical /
// Mental / Social), but with GK-shaped skills inside each. No age banding -
// one rubric across all age groups. Descriptors map to the existing 5-level
// scale (Developing → Impactful), with the user's 4-point coaching scale
// (Work on → Tuned) absorbed into levels 1–4 and "Impactful" added as the new
// top tier for standout, team-defining keepers.
//
// Storage shape differs from EVAL_DATA: GK has no age band, so the structure
// is `domain → skill → [5 descriptors]` directly (no `ageGroup` key in
// between). `getDescriptors` tries the GK rubric first by skill name and
// falls back to the field rubric - skill names never overlap between formats.
// -----------------------------------------------------------------------------

export const EVAL_DATA_GK = {

  Technical: {
    'Set position': [
      'Set late or inconsistent; stance unsettled before shot',
      'Sets most times; balance shaky under pressure',
      'Reaches set position reliably; balance + stick ready',
      'Set early; balanced platform; weight ready to drive either side',
      'Set position automatic; relaxed but explosive; cues opponent\'s choice',
    ],
    'Positioning': [
      'Caught out of position often; line and depth unclear',
      'Holds depth in static play; shifts late as ball moves',
      'Adjusts depth + angles with the ball reliably',
      'Anticipates shifts; reduces shooter\'s available net',
      'Reads play before it develops; opponent\'s options shrink at every touch',
    ],
    'Pad saves': [
      'Pad shape unstable; rebounds spill into danger',
      'Pad makes contact; control of rebound inconsistent',
      'Pad save reliable on direct shots; rebound mostly directed',
      'Pad shape strong; ball cushioned or directed wide of danger',
      'Pad save + controlled rebound or smother; minimises second chances',
    ],
    'Hand saves': [
      'Glove late or wrong-hand under pressure',
      'Hands sometimes; reflection direction inconsistent',
      'Hand save reliable on raised shots; ball cleared from danger',
      'Hand & stick position consistent; ball reflected wide or held',
      'Hand saves under pace; ball routinely directed to safe channels',
    ],
    'Stick saves': [
      'Stick out of position; reactive but unreliable',
      'Stick present; angle of save inconsistent',
      'Stick save reliable for shots and deflections',
      'Stick position dictates rebound direction; reset quick after',
      'Stick neutralises deflections + drag attempts at top of D',
    ],
    'Single slide': [
      'Slide late or unbalanced; commits without reading the play',
      'Reaches the shooter sometimes; timing inconsistent',
      'Reliable slide on 1v1; covers the ball',
      'Slide timed off the shooter\'s touch; closes the angle decisively',
      'Single slide ends the chance; recovery to feet immediate',
    ],
    'Ability to keep it out': [
      'Goals conceded that should be saved',
      'Saves expected shots; struggles under pressure or against pace',
      'Reliable on shots within the framework; saves what should be saved',
      'Saves the difficult ones; few "soft" goals',
      'Match-deciding saves; team holds shape because of it',
    ],
  },

  Tactical: {
    'Decision Making': [
      'Decisions late; commits early or stays back inconsistently',
      'Reads simple situations; complex moments hesitant',
      'Reliable read on come-out vs hold; rebound choice usually right',
      'Decisive 6-yard reads; clears risk before it develops',
      'Anticipates the next two passes; positions or commits one beat early',
    ],
    'Short corners': [
      'Set-up unclear; role with runners confused',
      'Knows role; execution shaky under pressure',
      'Reliable set-up + execution; communicates with runners',
      'Drives the set-up; quick reset after save or clearance',
      'Reads variation early; calm in the chaos; resets the team',
    ],
    'Shoot out': [
      'Off-balance early; commits without reading',
      'Survives some sequences; technique under pressure inconsistent',
      'Patient feet; uses save selection; reliable on standard sequences',
      'Forces shooter to commit first; consistent save shape',
      'Owns the moment; saves at a high rate; presence affects shooter choice',
    ],
  },

  Physical: {
    'Lateral agility / footwork': [
      'Heavy on feet; transfers weight late',
      'Moves but loses balance under speed',
      'Small steps reliable; weight ready to drive',
      'Quick lateral coverage; weight always under control',
      'Footwork makes saves possible others wouldn\'t reach',
    ],
    'Reaction speed': [
      'Late on close-range work; surprised by deflections',
      'Reacts on clean shots; struggles with second-phase',
      'Reliable reaction on top-circle hits + deflections',
      'Reflexes match pace of the game; consistent under pressure',
      'Reaction-save level changes the team\'s defensive risk tolerance',
    ],
    'Stability & strength': [
      'Knocked off shape in contact; save platform collapses',
      'Holds shape in static saves; contact disrupts',
      'Stable save platform; recovers shape after contact',
      'Strong in tackles, rebounds, jostles; holds ground reliably',
      'Physical presence felt; rarely moved off the save line',
    ],
    'Work rate': [
      'Inconsistent intensity; switches off between shots',
      'Engaged when busy; drops between phases',
      'Steady work rate through the session',
      'Repeats hard work; energy stays late',
      'Highest work rate on the pitch; lifts team standard',
    ],
  },

  Mental: {
    'Concentration': [
      'Switches off in quiet spells; surprised when ball returns',
      'Engaged in active play; drifts between phases',
      'Stays engaged through long quiet periods',
      'Reads play even when uninvolved; ready when ball arrives',
      'Concentration visible to teammates; quiet half made noisy fast',
    ],
    'Resilience': [
      'Visibly shaken after a goal; next save affected',
      'Recovers between phases; minor lingering effect',
      'Reset quickly after conceding; next save reliable',
      'Bounce-back automatic; performance lifts after errors',
      'Goal conceded = next save more likely; mindset under any pressure',
    ],
    'Coachability': [
      'Slow to apply feedback; repeats errors',
      'Applies feedback after multiple cues',
      'Adjusts within drills; takes coaching in stride',
      'Asks questions; applies feedback fast',
      'Coaches teammates with what they\'ve learned',
    ],
    'Body language after saves': [
      'Slumped or hesitant after saves; doubt visible',
      'Recovers physically; energy flat',
      'Strong reset position; ready for next phase',
      'Confident body language lifts defenders',
      'Presence after saves becomes team-defining',
    ],
  },

  Social: {
    'Communication / Command of the D': [
      'Quiet; teammates organise without GK input',
      'Some calls; lacks clarity in chaos',
      'Clear calls in routine play; reliable on corners + dead balls',
      'Owns the D; defenders set up off GK voice',
      'Whole team plays one beat ahead because of GK voice',
    ],
    'Attendance': [
      'Inconsistent at training; GK-specific sessions missed',
      'Present mostly; punctuality patchy',
      'Reliable training attendance; on time for GK work',
      'Always present; prepares early; supports keeper group',
      'Sets the standard; teammates raise their attendance because of it',
    ],
    'Respect & sportsmanship': [
      'Frustration with own play affects teammates',
      'Composed in routine play; conflict reveals frustration',
      'Composed under pressure; respectful to opponents and officials',
      'Sets the example after conceding; supports teammates\' errors',
      'Calmest player on the pitch; conduct defines team standard',
    ],
  },

};

// -----------------------------------------------------------------------------
// Public helpers - format-aware where it matters, smart-fallback where it
// doesn't. Skill names never overlap between formats, so `getDescriptors` can
// look up by skill name without an explicit format hint.
// -----------------------------------------------------------------------------

// Get all domain names in display order (same names for both formats).
export function getDomains() {
  return Object.keys(EVAL_DATA);
}

// Get all skills for a domain in the FIELD rubric (back-compat default).
export function getSkills(domain) {
  return Object.keys(EVAL_DATA[domain] || {});
}

// Get all skills for a domain in a specific rubric format.
/**
 * The stable id of a criterion: what a mark is actually joined to.
 *
 * A criterion's DISPLAY NAME is free to change. Its id is not, because every
 * mark ever made points at it (16,044 rating rows), as do the DTA content tags
 * and the per-pillar coach permissions. Rename "Ball carrying" while the name
 * is the join key and four thousand marks detach in silence.
 *
 * The format is part of the id because the two rubrics genuinely disagree:
 * field Tactical/Positioning and keeper Technical/Positioning are different
 * things that happen to share a word.
 *
 * MUST MATCH the slugs seeded into rubric_criteria by
 * supabase/migrations/20261029170000_criteria_get_stable_ids.sql. The tests
 * pin several exact values for that reason.
 *
 * Returns null for anything that is not in the rubric, rather than inventing
 * an id. A null is recoverable; a made-up id fails the foreign key and loses
 * the coach the whole evaluation they just typed.
 */
const _slug = (s) => String(s)
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

export function criterionIdFor(domain, skill, format = 'field_v1') {
  const data = format === 'gk_v1' ? EVAL_DATA_GK : EVAL_DATA;
  const d = String(domain || '').trim();
  const sk = String(skill || '').trim();
  const known = data[d] && Object.prototype.hasOwnProperty.call(data[d].skills || data[d], sk);
  if (!known) return null;
  return `${format === 'gk_v1' ? 'gk' : 'field'}.${_slug(d)}.${_slug(sk)}`;
}

export function getSkillsFor(domain, format) {
  if (format === 'gk_v1') return Object.keys(EVAL_DATA_GK[domain] || {});
  return Object.keys(EVAL_DATA[domain] || {});
}

// Get descriptors for a skill, in the active language (or an explicit `lang`).
// Dutch falls back to English per LINE, so a partly-translated rubric never
// shows blanks. GK rubric is tried first (skill names never overlap).
export function getDescriptors(domain, skill, ageGroup, lang, asOf) {
  const L = lang || CONFIG.i18n?.lang || 'en';

  // Precedence: the club's own wording, then the Dutch overlay, then the
  // standard English. A club override outranks the translation on purpose:
  // rewriting a descriptor changes what the LEVEL means, so the standard
  // Dutch line would be describing something else.
  const gk = EVAL_DATA_GK[domain]?.[skill];
  if (gk) {
    return gk.map((en, i) =>
      _override(domain, skill, '', i + 1, L, 'gk_v1', asOf)
      ?? ((L === 'nl') ? _nl(_gkKey(domain, skill, i)) || en : en));
  }
  const field = EVAL_DATA[domain]?.[skill]?.[ageGroup] || [];
  return field.map((en, i) =>
    _override(domain, skill, ageGroup, i + 1, L, 'field_v1', asOf)
    ?? ((L === 'nl') ? _nl(_fieldKey(domain, skill, ageGroup, i)) || en : en));
}

// Display name of a domain / skill in the active language (English fallback).
export function localizeDomain(domain, lang) {
  const L = lang || CONFIG.i18n?.lang || 'en';
  return (L === 'nl' && _nl(_domNameKey(domain))) || domain;
}
export function localizeSkill(skill, lang) {
  const L = lang || CONFIG.i18n?.lang || 'en';
  return (L === 'nl' && _nl(_skillNameKey(skill))) || skill;
}

// The canonical list of every translatable string (English source), used by the
// round-trip tool to build the reviewer's Excel and detect stale lines. One row
// per domain name, skill name, and descriptor (field per age group + GK).
export function buildTranslatableEntries() {
  const out = [];
  const seenSkill = new Set();
  Object.entries(EVAL_DATA).forEach(([domain, skills]) => {
    out.push({ key: _domNameKey(domain), type: 'name', domain, skill: '', ageGroup: '', level: '', english: domain });
    Object.entries(skills).forEach(([skill, ages]) => {
      if (!seenSkill.has(skill)) { seenSkill.add(skill); out.push({ key: _skillNameKey(skill), type: 'name', domain, skill, ageGroup: '', level: '', english: skill }); }
      Object.entries(ages).forEach(([ageGroup, arr]) => {
        arr.forEach((english, i) => out.push({ key: _fieldKey(domain, skill, ageGroup, i), type: 'field', domain, skill, ageGroup, level: i + 1, english }));
      });
    });
  });
  Object.entries(EVAL_DATA_GK).forEach(([domain, skills]) => {
    Object.entries(skills).forEach(([skill, arr]) => {
      if (!seenSkill.has(skill)) { seenSkill.add(skill); out.push({ key: _skillNameKey(skill), type: 'name', domain, skill, ageGroup: '', level: '', english: skill }); }
      arr.forEach((english, i) => out.push({ key: _gkKey(domain, skill, i), type: 'gk', domain, skill, ageGroup: '', level: i + 1, english }));
    });
  });
  return out;
}

// Get the total number of criteria across all domains in a given format.
export function getTotalCriteria(format) {
  const data = format === 'gk_v1' ? EVAL_DATA_GK : EVAL_DATA;
  return Object.values(data).reduce(
    (total, domain) => total + Object.keys(domain).length, 0
  );
}

// Pick the rubric format for a player.
export function getEvalFormat(player) {
  return (player && player.is_goalkeeper) ? 'gk_v1' : 'field_v1';
}
